/**
 * 组织模型清单类型（单一真相源，红线 #9）——N-167 起按成本档分键。
 *
 * 产品白话：组织管理员可以按档（经济 / 标准 / 强力）整份换掉平台的成本档清单——每一档是一份【有序模型
 * 清单】，第一名欠费 / 挂了，系统自动用第二名；还可以给某类服务端任务换档（`taskTypeTiers`）。服务端经统一
 * 选模型函数（`task-model-chain.ts`）消费；桌面本地单次成文读经济档。
 *
 * 与 analyze_media / generate_image 两桶的策展（{@link PlatformToolCuration}，tenant 级）概念对齐：ORDERED
 * 清单 = 优先级，首项 = 默认首选；瞬时失败沿链前进、单向防回环。本文件只承载 per-org 这份。
 */
import { COST_TIERS, isCostTier } from "./model-cost-tiers.js";
import { isOrgConfigurableTaskType, isTaskTypeId } from "./task-type-catalog.js";
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
/** 能力桶的「值班表」标识——审计字段 `requestedTier` 记录本次任务归属哪个桶。 */
export const MODEL_BUCKET_TIERS = ["text_generate", "agent_main"];
/** 反序列化边界防御（红线 #9）：清洗一个未知输入成规范候选链，丢弃无 model 的坏行。
 *  返回 undefined 表示「未配置该桶」（消费方回落单模型），而非空链（空链语义等价）。 */
function sanitizeCandidates(raw) {
    // 允许两种入参形态：数组（直接是候选列表）或 { candidates: [...] } 包裹。
    const list = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.candidates)
            ? raw.candidates
            : null;
    if (!list)
        return undefined;
    const candidates = [];
    for (const it of list) {
        const model = typeof it?.model === "string"
            ? (it.model).trim()
            : "";
        if (!model)
            continue; // 坏行（缺 model）跳过，绝不裸抛（红线 #9）
        const note = typeof it?.note === "string"
            ? it.note
            : undefined;
        candidates.push(note !== undefined ? { model, note } : { model });
    }
    return { candidates };
}
/**
 * **唯一构建器**（红线 #9）：从原始输入（org 配置读盘/网络反序列化）构造规范
 * `ModelBucketCuration`。逐桶反序列化防御——坏行丢弃、缺失落 undefined（未配置），
 * 绝不裸抛。生产方（per-org curation source）与任何第二生产者都接这一个构建器，
 * 不各自手搓 object literal。
 */
export function buildModelBucketCuration(input) {
    const obj = isRecord(input) ? input : {};
    const out = {};
    for (const tier of COST_TIERS) {
        const bucket = sanitizeCandidates(obj[tier]);
        // 空数组 = 跟随平台，归一为不写这个键（与「键不存在」同义，消费方不必区分两种）。
        if (bucket && bucket.candidates.length > 0)
            out[tier] = bucket;
    }
    const taskTypeTiers = sanitizeTaskTypeTiers(obj.taskTypeTiers);
    if (taskTypeTiers)
        out.taskTypeTiers = taskTypeTiers;
    return out;
}
/**
 * 组织清单里有没有任何内容（单一判据）：三档任一在场或配了 `taskTypeTiers`。入参须已过
 * {@link buildModelBucketCuration}（空候选已归一为删键，所以「键在场」= 非空）。迁移前的旧形态
 * （`textGenerate` / `agentMain`）经构建器后什么都不剩，一律算「没有」= 跟随平台。
 */
export function hasModelBucketCurationContent(curation) {
    return COST_TIERS.some((tier) => curation[tier] !== undefined) || curation.taskTypeTiers !== undefined;
}
/**
 * 组织给任务类型换的档：未知类型 id、组织不能换档的类型（跟随主脑的飞书两类，{@link isOrgConfigurableTaskType}）、
 * 未知档的条目丢弃（写入路由另行拒收），剩空 = 未配置。
 */
function sanitizeTaskTypeTiers(raw) {
    if (!isRecord(raw))
        return undefined;
    const out = {};
    let any = false;
    for (const [taskType, tier] of Object.entries(raw)) {
        if (!isTaskTypeId(taskType) || !isOrgConfigurableTaskType(taskType) || !isCostTier(tier))
            continue;
        out[taskType] = tier;
        any = true;
    }
    return any ? out : undefined;
}
/** 组织在这一档整份替换的候选；没换（或空）返回 undefined = 跟随平台。 */
export function orgCostTierCandidates(curation, tier) {
    const candidates = curation?.[tier]?.candidates;
    return candidates && candidates.length > 0 ? candidates : undefined;
}
/**
 * 组装单次成文的**生效候选链**：任务钉的模型（override）置链首，其后接 host 给的候选，
 * 去重（首次出现胜出，保序）。
 *
 * 返回值只含**具体模型 id**；**空数组 = 没有任何候选**（既无 override、host 也没给）→ 消费方据此
 * 单发一次 host 默认。有 override 无候选 → `[{model: pinned}]`。纯函数、无副作用——core 单次成文执行器
 * 用它组链（企业后端的候选来自统一选模型函数，锁定时只有同一个模型；桌面是组织经济档）。
 */
export function buildBucketChain(pinnedModel, bucket) {
    const curated = bucket?.candidates ?? [];
    const pinned = pinnedModel?.trim() || undefined;
    const chain = [];
    const seen = new Set();
    if (pinned) {
        chain.push({ model: pinned });
        seen.add(pinned);
    }
    for (const c of curated) {
        if (!c.model || seen.has(c.model))
            continue;
        seen.add(c.model);
        chain.push(c);
    }
    return chain; // 空 = 无显式模型 → 消费方回落 host 默认单发（零破坏）
}
//# sourceMappingURL=model-bucket-curation.js.map